import { Box } from '@mui/material';

import TabBar, { type TabBarItem } from '@/designs-dev/device/TabBar';
import { TabBarVisibilityProvider } from '@/designs-dev/device/TabBarVisibility';
import ShortlistsTab, {
  type ShortlistsInitial,
} from '@/karehero-app/care/shortlists/ShortlistsTab';
import { type PublishedShortlist } from '@/karehero-app/care/shortlists/fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

// The member-app tabs for this version: a Profile tab in the middle and a
// dedicated Care tab. Named for what the family came for rather than the act of
// looking for it — they tap it to get on with arranging care, not to run a search
// — and a heart rather than a magnifying glass for the same reason.
//
// Spelled out here rather than taken from the default because this version's
// tabs are inert: the id is 'search' so no tab reads as one you could go to.
const NAV_ITEMS: TabBarItem[] = [
  { id: 'home', label: 'Home', icon: 'house', solid: true },
  { id: 'actions', label: 'Actions', icon: 'calendar', solid: true },
  { id: 'profile', label: 'Profile', icon: 'user', solid: false },
  { id: 'search', label: 'Care', icon: 'heart', solid: true },
  { id: 'help', label: 'Help', icon: 'circle-question', solid: false },
];

/**
 * Shortlists V1: shortlists promoted from a Care tool (Care → Tools → a pushed
 * stack) to a primary bottom-nav tab. The proposal the master app now carries —
 * this stays as the harness for it, because the states (empty, error, provider
 * drawer, booking sheet) are addressable here and the tabs are inert.
 */
// This version deliberately traps the user in the care flow: the other tabs and
// the app-bar bell are inert, so the only navigation is into a shortlist and
// back. That's why there's no routing callback here.
const ShortlistsV1Screen = ({
  shortlists,
  error = false,
  initial,
}: {
  // Harness-only: override the published shortlists (e.g. [] for the empty
  // state) or force the load error, so the flows page can show those states with
  // the full tab bar + app bar chrome.
  shortlists?: PublishedShortlist[];
  error?: boolean;
  // Harness-only: seed which screen/state is open on mount (see ShortlistsInitial).
  initial?: ShortlistsInitial;
} = {}) => (
  <TabBarVisibilityProvider>
    {(navHidden) => (
      <ScreenRoot>
        {/* The status bar comes from ShortlistHome, which is a screen in its
            own right. */}
        <ShortlistsTab
          shortlists={shortlists}
          error={error}
          initial={initial}
          // Inert bell: it stays on the app bar, it just doesn't lead anywhere.
          onNotifications={() => {}}
        />

        {/* Collapses away while a sheet or pushed page is open. The tabs are
            inert here — no onSelect, so the care flow can't be left. */}
        <Box
          sx={{
            maxHeight: navHidden ? 0 : 140,
            overflow: 'hidden',
            flexShrink: 0,
            transition: 'max-height 280ms ease',
          }}
        >
          <TabBar active='search' items={NAV_ITEMS} />
        </Box>
      </ScreenRoot>
    )}
  </TabBarVisibilityProvider>
);

ShortlistsV1Screen.displayName = 'ShortlistsV1Screen';
export default ShortlistsV1Screen;
